// Town tree canopies: clumped painterly leaf masses (the landscape lane's bush pattern, scaled up).
// Each canopy is several overlapping rounded clusters; each cluster is a shell of leaf-cluster
// cards from leaves.webp (3x3 cells) set round a sphere, facing out, with normals radiating from
// the cluster centre so it shades as one rounded volume. Vertex colour carries the value: a
// near-black olive core and underside lifting to sunlit olive-green tops. One draw (+ shadow).

import * as THREE from 'three';
import { HAZE, withHaze } from '../materials';
import { sharedTexture } from '../warm';

/** One leaf cluster: centre, radius, a 0..1 tone jitter. */
export interface Canopy { x: number; y: number; z: number; r: number; tone: number; /** ground weeds: greener, no yellow crown */ weed?: boolean }

const CELLS = [[0, 2], [2, 2], [0, 1], [1, 1], [2, 1], [0, 0], [1, 0], [2, 0]];

function hashRand(seed: number) {
  let s = seed >>> 0 || 1;
  return () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 4294967296; };
}

export function canopyGeometry(list: Canopy[], quality: number) {
  const cards = quality <= 0 ? 10 : quality === 1 ? 14 : 18;
  const pos: number[] = [], nrm: number[] = [], col: number[] = [], uv: number[] = [], sw: number[] = [], idx: number[] = [];
  const DARK0 = [0.025, 0.04, 0.015], MID0 = [0.2, 0.32, 0.08], LIT0 = [0.62, 0.78, 0.2];
  const DARKW = [0.03, 0.06, 0.025], MIDW = [0.1, 0.2, 0.06], LITW = [0.22, 0.38, 0.1];
  let DARK = DARK0, MID = MID0, LIT = LIT0, cv = 0;
  const card = (cx: number, cy: number, cz: number, rx: number, ry: number, rz: number, ux: number, uy: number, uz: number,
    sz: number, C: [number, number, number], r: number, rnd: () => number) => {
    const cell = CELLS[(rnd() * CELLS.length) | 0];
    const flip = rnd() < 0.5 ? -1 : 1;
    const v0 = pos.length / 3;
    for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
      const px = cx + (rx * sx + ux * sy) * sz, py = cy + (ry * sx + uy * sy) * sz, pz = cz + (rz * sx + uz * sy) * sz;
      pos.push(px, py, pz);
      // normals radiate from the cluster centre, nudged up: the clump shades as one ball
      const nx = px - C[0], ny = py - C[1] + r * 0.25, nz = pz - C[2], nl = Math.hypot(nx, ny, nz) || 1;
      nrm.push(nx / nl, ny / nl, nz / nl);
      // value: dark underside / core, lit crown; deeper toward the centre
      // value follows the card centre more than the vertex: a big card hanging under the clump stays dark
      const h = 0.72 * (cy - C[1]) / r + 0.28 * (py - C[1]) / r, d = Math.min(1, Math.hypot(px - C[0], py - C[1], pz - C[2]) / r);
      const up = Math.max(0, Math.min(1, (h + 0.7) / 1.5));
      // only the core sinks to dark: the outer shell stays mid, the crown goes sunny; each clump
      // carries its own value so the mass reads as light and dark clusters
      const k = Math.min(1, Math.max(0, 0.18 + 0.5 * up + 0.5 * (d - 0.45) + cv));
      const a = k < 0.5 ? DARK : MID, b = k < 0.5 ? MID : LIT, f = k < 0.5 ? k * 2 : k * 2 - 1;
      const j = 0.85 + 0.3 * rnd();
      col.push((a[0] + (b[0] - a[0]) * f) * j, (a[1] + (b[1] - a[1]) * f) * j, (a[2] + (b[2] - a[2]) * f) * j);
      uv.push((cell[0] + (flip * sx + 1) / 2) / 3, (cell[1] + (sy + 1) / 2) / 3);
      sw.push(d * r * 0.06);
    }
    idx.push(v0, v0 + 1, v0 + 2, v0, v0 + 2, v0 + 3);
  };
  for (const c of list) {
    const rnd = hashRand(Math.floor(c.x * 131 + c.y * 977 + c.z * 4271) ^ 0x9e3779b9);
    const C: [number, number, number] = [c.x, c.y, c.z];
    cv = (c.tone - 0.5) * 0.3;
    DARK = c.weed ? DARKW : DARK0; MID = c.weed ? MIDW : MID0; LIT = c.weed ? LITW : LIT0;
    const n = Math.round(cards * Math.min(1.3, Math.max(0.7, c.r / 1.6)));
    for (let i = 0; i < n; i++) {
      // golden-angle spread over the sphere, weighted to the crown and the sides
      const t = (i + 0.5) / n;
      const el = Math.asin(-0.55 + 1.5 * t) + (rnd() - 0.5) * 0.25;
      const az = i * 2.39996 + c.tone * 6.28 + (rnd() - 0.5) * 0.4;
      const dx = Math.cos(el) * Math.cos(az), dy = Math.sin(el), dz = Math.cos(el) * Math.sin(az);
      const rr = c.r * (0.62 + 0.2 * rnd());
      const cx = c.x + dx * rr, cy = c.y + dy * rr * 0.82, cz = c.z + dz * rr;
      const sz = c.r * (0.62 + 0.2 * rnd());
      // card axes: right = horizontal tangent, up = world up tilted toward the outward normal
      const rx = -Math.sin(az), rz = Math.cos(az);
      let ux = -dx * dy * 0.7, uy = 1 - Math.abs(dy) * 0.55, uz = -dz * dy * 0.7;
      const ul = Math.hypot(ux, uy, uz); ux /= ul; uy /= ul; uz /= ul;
      // a little roll so the leaf clumps don't line up
      const roll = (rnd() - 0.5) * 0.9, cr = Math.cos(roll), sr = Math.sin(roll);
      const Rx = rx * cr + ux * sr, Ry = uy * sr, Rz = rz * cr + uz * sr;
      const Ux = ux * cr - rx * sr, Uy = uy * cr, Uz = uz * cr - rz * sr;
      card(cx, cy, cz, Rx, Ry, Rz, Ux, Uy, Uz, sz, C, c.r, rnd);
    }
    // a dark crossed core so the clump never reads hollow between its shell cards
    for (let k = 0; k < 2; k++) {
      const az = c.tone * 3 + k * 1.5708;
      card(c.x, c.y - c.r * 0.1, c.z, Math.cos(az), 0, Math.sin(az), 0, 1, 0, c.r * 0.7, C, c.r, rnd);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('aSway', new THREE.Float32BufferAttribute(sw, 1));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

const FRAG_LEAF = /* glsl */ `
  {
    vec4 t = texture2D( map, vMapUv );
    if ( t.a < 0.3 ) discard;
    diffuseColor.a = smoothstep( 0.3, 0.7, t.a ); // feeds alpha-to-coverage where MSAA is on
    // keep the hue in the vertex colour; the texture only paints the leaf-clump value
    float l = dot( t.rgb, vec3( 0.3, 0.55, 0.15 ) );
    diffuseColor.rgb *= 0.7 + 1.0 * l;
  }
`;

export function canopyMaterial(leaves: THREE.Texture) {
  const m = new THREE.MeshLambertMaterial({ color: 0xffffff, vertexColors: true, side: THREE.DoubleSide, map: leaves, alphaToCoverage: true });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = HAZE.uTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aSway;\nuniform float uTime;')
      .replace('#include <begin_vertex>', /* glsl */ `#include <begin_vertex>
        transformed.x += sin( uTime * 1.3 + position.x * 0.4 + position.z * 0.3 ) * aSway;
        transformed.y += sin( uTime * 1.9 + position.z * 0.5 ) * aSway * 0.4;
        transformed.z += cos( uTime * 1.1 + position.x * 0.3 ) * aSway * 0.7;`);
    shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', FRAG_LEAF);
  };
  withHaze(m, 0.6);
  m.customProgramCacheKey = () => 'townCanopy';
  return m;
}

export function canopyDepthMaterial(leaves: THREE.Texture) {
  return new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking, map: leaves, alphaTest: 0.5, side: THREE.DoubleSide });
}

export function loadLeaves(quality: number, maxAniso: number) {
  const base = (import.meta as any).env?.BASE_URL ?? '/';
  const aniso = quality >= 2 ? Math.min(4, maxAniso) : 1;
  // linear on purpose: only its value is used (shared across islands)
  return sharedTexture(base + 'assets/landscape/leaves.webp', `lin${aniso}`, (t) => { t.anisotropy = aniso; });
}
